import * as PIXI from 'pixi.js'
import gsap from 'gsap'

import AssetsManager from '../../../pixi/AssetsManager'
import CardView from './CardView'
import CardModel from '../data/CardModel'
import TouchUtil from '../../../util/TouchUtil'

/**
 * StockAreaクラス
 */
class StockArea extends PIXI.Container {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //コンテナ
  private _stockContainer: PIXI.Container
  private _openContainer: PIXI.Container

  //ヒットエリア
  private _hitArea: PIXI.Graphics

  //bindされたカード選択メソッド
  private _onCardSelect: any

  // ---------------------------------------------------------------------------------------------------------
  // --	StockArea
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor(models: Array<CardModel>) {
    super()

    //カード選択時の処理
    this._onCardSelect = this.onCardSelect.bind(this)

    //影
    const shadow: PIXI.Sprite = AssetsManager.instance.createSpriteByName('card_shadow.png')
    shadow.anchor.set(0.5)
    shadow.scale.set(3.0)
    shadow.alpha = 0.2
    this.addChild(shadow)

    //リロードアイコン
    const reload: PIXI.Sprite = AssetsManager.instance.createSpriteByName('reload.png')
    reload.anchor.set(0.5)
    reload.scale.set(3.0)
    this.addChild(reload)

    //ストック用コンテナ配置
    this._stockContainer = new PIXI.Container()
    this.addChild(this._stockContainer)

    //カード配置
    for (let index = 0; index < models.length; index++) {
      const model: CardModel = models[index]
      const card: CardView = model.view
      card.x = 0
      card.y = -0.5 * index
      this._stockContainer.addChild(card)
      card.on('pointerdown', this._onCardSelect)
    }

    //オープン用コンテナ配置
    this._openContainer = new PIXI.Container()
    this.addChild(this._openContainer)

    //ヒットエリア配置
    const rect = shadow.getBounds()
    this._hitArea = new PIXI.Graphics()
    this._hitArea.beginFill(0x000000)
    this._hitArea.drawRect(rect.x, rect.y, rect.width, rect.height)
    this._hitArea.endFill()
    this.addChild(this._hitArea)
    this._hitArea.alpha = 0.0
    this._hitArea.interactive = true
    this._hitArea.cursor = 'pointer'
    this._hitArea.on('pointerdown', this.onStockClick.bind(this))
  }

  /**
   * リロード
   */
  reload(): void {
    //ボタンを押せなくしておく
    this._hitArea.interactive = false

    //カードを戻す
    this.backToStock()
  }

  /**
   * ストックする
   */
  backToStock(): void {
    if (this._openContainer.children.length == 0) {
      gsap.delayedCall(0.5, () => {
        this._hitArea.interactive = true
      })
      return
    }

    //一番上のカードを取得
    const card: CardView = this._openContainer.getChildAt(
      this._openContainer.children.length - 1
    ) as CardView

    //ストック用コンテナに移動
    this._stockContainer.addChild(card)

    //アニメーション
    gsap.killTweensOf(card)
    gsap.to(card, {
      x: 0,
      y: -0.5 * (this._stockContainer.children.length - 1),
      duration: 0.02,
      ease: 'power2.inOut',
      onStart: () => {
        card.flip(false)
      },
      onComplete: () => {
        this.backToStock()
      },
    })
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	INPUT
  // ---------------------------------------------------------------------------------------------------------

  /**
   * ストック選択
   */
  public onStockClick(e: PIXI.FederatedPointerEvent): void {
    //ストックが０枚の時はリロード
    if (this._stockContainer.children.length == 0) {
      this.reload()
      return
    }

    //一番上のカードを取得
    const card: CardView = this._stockContainer.getChildAt(
      this._stockContainer.children.length - 1
    ) as CardView

    //オープン用コンテナに移動
    this._openContainer.addChild(card)

    //アニメーション
    gsap.killTweensOf(card)
    gsap.to(card, {
      x: -card.width,
      y: -0.5 * (this._openContainer.children.length - 1),
      duration: 0.5,
      ease: 'power2.inOut',
    })
    card.flip(true)

    //SE
    AssetsManager.instance.playSound('sound_mekuru')
  }

  /**
   * カード選択
   */
  public onCardSelect(e: PIXI.FederatedPointerEvent): void {
    //タッチID保持
    if (TouchUtil.touchID != -1) return
    TouchUtil.touchID = e.pointerId

    //タッチ位置を保存
    TouchUtil.global.copyFrom(e.global)

    //カードの参照
    const card: CardView = e.target as CardView

    //リセット
    card.off('pointerdown')
    gsap.killTweensOf(card)

    //イベント発行
    this.emit('SELECT_CARD' as any, { target: this, cards: [card] })
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	METHODS
  // ---------------------------------------------------------------------------------------------------------

  /**
   * カードを追加する
   */
  public addCards(cards: Array<CardView>): void {
    //カードをコンテナに追加
    for (let i = 0; i < cards.length; i++) {
      //カード参照
      const card: CardView = cards[i]

      //ローカル座標に変換して配置
      const globalCardPos: PIXI.Point = card.parent.toGlobal(card.position)
      const localPos: PIXI.Point = this.toLocal(globalCardPos)
      card.x = localPos.x
      card.y = localPos.y
      this._openContainer.addChild(card)

      //アニメーション
      gsap.to(card, {
        x: -card.width,
        y: -0.5 * (this._openContainer.children.length - 1),
        duration: 0.1,
        ease: 'power2.out',
        onComplete: () => {
          card.on('pointerdown', this._onCardSelect)
        },
      })
    }
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default StockArea
